/**
 * Content for the "Remember password" alert in the New User app, which is shown
 * to get the user to confirm the password they have just been provided with
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { shouldKeyTrigger } from 'phoenix2/utils/keyboard';

class RememberPasswordAlert extends React.Component {
    static displayName = 'RememberPasswordAlert';

    static propTypes = {
        // Callback for when the alert is dismissed
        onEndAlert: PropTypes.func.isRequired,

        // The user's new password which they are required to enter to finish
        // this alert
        newPassword: PropTypes.string.isRequired,

        // The user's username which they are required to enter to finish this alert
        username: PropTypes.string,
    };

    constructor(props) {
        super(props);

        this.state = {
            // Stores the password and username the user is entering to confirm it
            confirmedPassword: '',
            confirmedUsername: '',
        };
    }

    render() {
        const { newPassword, onEndAlert, username } = this.props;
        const { confirmedPassword, confirmedUsername } = this.state;

        const disableFinishButton =
            newPassword !== confirmedPassword || username !== confirmedUsername;
        const finishButtonClassnames = classNames('btn', 'alert-button', {
            'btn-disabled': disableFinishButton,
        });

        return (
            <div className="remember-password-alert">
                <div className="alert-message">Make sure you&rsquo;ve remembered your details.</div>
                <div className="alert-message">
                    This is the only time you will be told these details.
                </div>
                <div className="alert-message remember-password-to-finish">
                    To finish, enter your details below:
                </div>
                <input
                    className="remember-password-password-input"
                    value={confirmedUsername}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    type="text"
                    name="username"
                    placeholder="Username"
                    onChange={evt => this._onUsernameChanged(evt)}
                />
                <input
                    className="remember-password-password-input"
                    value={confirmedPassword}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    type="text"
                    name="password"
                    placeholder="Password"
                    onChange={evt => this._onPasswordChanged(evt)}
                />
                <div className="alert-buttons">
                    <div
                        tabIndex="0"
                        key="cancel"
                        className="btn btn-secondary alert-button"
                        onClick={() => onEndAlert('back')}
                        onMouseDown={evt => {
                            // Prevent focussing when clicked:
                            evt.preventDefault();
                        }}
                        onKeyDown={evt => shouldKeyTrigger(evt) && onEndAlert('back')}
                    >
                        Back
                    </div>
                    <div
                        tabIndex={disableFinishButton ? '-1' : '0'}
                        key="enter"
                        className={finishButtonClassnames}
                        disabled={disableFinishButton}
                        onClick={() => !disableFinishButton && this.props.onEndAlert('finish')}
                        onMouseDown={evt => {
                            // Prevent focussing when clicked:
                            evt.preventDefault();
                        }}
                        onKeyDown={evt =>
                            shouldKeyTrigger(evt) &&
                            !disableFinishButton &&
                            this.props.onEndAlert('finish')
                        }
                    >
                        Finish
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Called when the user changes the value in the password input field
     * @param evt
     * @private
     */
    _onPasswordChanged(evt) {
        this.setState({
            confirmedPassword: evt.target.value,
        });
    }

    /**
     * Called when the user changes the value in the username input field
     * @param evt
     * @private
     */
    _onUsernameChanged(evt) {
        this.setState({
            confirmedUsername: evt.target.value,
        });
    }
}

export { RememberPasswordAlert };
